import SEO from "@/components/SEO";
import ArticleHero from "@/components/ArticleHero";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import { SITE_URL } from "@/lib/site";
import { Helmet } from "react-helmet-async";

const PATH = "/glossary";
const TITLE = "Glossary";
const DESCRIPTION =
  "Plain-English definitions of the terms every patient choosing a cosmetic-dentistry clinic in Spain should know — materials, workflow, preparation, optics, occlusion.";

interface Term {
  term: string;
  definition: string;
}

// Alphabetical. Every patient scanning Spain Smile Guide should be able
// to find the term here without needing a dental textbook.
const TERMS: Term[] = [
  {
    term: "Articulator",
    definition:
      "A mechanical device that holds upper and lower dental models in the relationship they occupy in the patient's mouth. Used by ceramists and dentists to design restorations that bite correctly once delivered.",
  },
  {
    term: "Bite registration",
    definition:
      "A physical record — typically a wax or silicone bite — that captures how the patient's upper and lower teeth meet. It travels with the impression to the lab so the restoration is built to the correct occlusion.",
  },
  {
    term: "Bonding",
    definition:
      "The chemical and micromechanical process by which a porcelain veneer is permanently attached to a prepared tooth surface. Enamel bonding is the strongest and most predictable; dentine bonding is weaker and less stable over decades.",
  },
  {
    term: "Bruxism",
    definition:
      "Habitual clenching or grinding of the teeth, often during sleep. A significant variable in cosmetic dentistry because it loads veneers and crowns in ways that can chip or fracture them, and frequently calls for a protective nightguard after treatment.",
  },
  {
    term: "Cementation",
    definition:
      "The clinical step in which the veneer is permanently fixed to the tooth after the try-in has been approved. Once cementation is complete, the case is no longer easily reversible.",
  },
  {
    term: "Chamfer preparation",
    definition:
      "A tooth-preparation design that creates a rounded, well-defined margin at the gumline. Favoured for conventional veneers and crowns because it gives the ceramist a clean, readable edge to finish against.",
  },
  {
    term: "Characterisation",
    definition:
      "The fine optical detailing a ceramist applies to a veneer during the firing stages — subtle variations in shade, translucency, surface texture, and enamel cracks that make the finished tooth read as a real tooth rather than a uniform block of ceramic.",
  },
  {
    term: "Chroma",
    definition:
      "The saturation or intensity of a tooth's colour, independent of how light or dark it is. A tooth can be low-value (dark) and low-chroma (greyish) at the same time; shade selection has to account for both axes.",
  },
  {
    term: "Cone-beam CT (CBCT)",
    definition:
      "A low-dose, three-dimensional dental X-ray used to plan implant placement, evaluate bone volume, and assess the relationship between teeth, roots, and surrounding anatomy. Standard in complex cases; not required for straightforward veneer work.",
  },
  {
    term: "Crown",
    definition:
      "A restoration that covers the entire external surface of a tooth, typically after substantial loss of tooth structure. A crown is more invasive than a veneer; the two are sometimes confused when preparation depth crosses into crown territory.",
  },
  {
    term: "DSD (Digital Smile Design)",
    definition:
      "A planning workflow in which photographs and digital scans of a patient are used to design the proposed smile on a computer before any tooth is touched. A good DSD workflow ends in a mock-up that can be tried in the mouth and adjusted before irreversible preparation begins.",
  },
  {
    term: "e.max",
    definition:
      "Brand name for a family of lithium-disilicate ceramics produced by Ivoclar Vivadent, including pressed (e.max Press) and milled (e.max CAD) variants. One of the most frequently used materials in contemporary veneer and crown work.",
  },
  {
    term: "e.max Press",
    definition:
      "The pressed-ingot form of e.max lithium disilicate. The ceramist presses a heated ingot into a mould made from a wax-up, which produces a homogeneous restoration well suited to posterior teeth and to minimally-prepared veneers.",
  },
  {
    term: "Feather-edge preparation",
    definition:
      "A very shallow preparation design in which the margin tapers to almost nothing, without a distinct step. Associated with ultra-thin no-prep and minimal-prep veneers; technique-sensitive, and less forgiving at the margin than a chamfer.",
  },
  {
    term: "Feldspathic porcelain",
    definition:
      "A traditional, finely-layered dental porcelain made from feldspar-based glass. Hand-built up on a refractory die by a ceramist in several firings. Favoured for its optical realism in anterior veneer cases; weaker than lithium disilicate, and relies on a good bond to enamel for durability.",
  },
  {
    term: "Hue",
    definition:
      "The basic colour family of a tooth — reddish-brown, yellow-brown, grey, reddish-grey — independent of brightness or saturation. The Vita Classical shade guide classifies hue by letter (A, B, C, D).",
  },
  {
    term: "Inlay",
    definition:
      "A ceramic or composite restoration that sits inside the prepared cavity of a tooth, replacing lost tissue but not covering the cusps. Less invasive than an onlay or a crown.",
  },
  {
    term: "Intraoral scan",
    definition:
      "A digital, optical impression of the teeth and soft tissues taken with a handheld scanner, producing a 3D model the lab can work from without a physical impression. Now standard at most serious cosmetic practices.",
  },
  {
    term: "Layering",
    definition:
      "The ceramist's process of building a porcelain restoration in successive thin coats of different shades and translucencies, firing between coats. Hand-layered veneers reproduce the optical depth of a natural tooth in a way monolithic blocks typically cannot.",
  },
  {
    term: "Lab script",
    definition:
      "The written prescription sent from the dentist to the ceramist, specifying material, shade, design notes, occlusion, and any case-specific requests. The quality of the lab script is a direct input into the quality of the finished case.",
  },
  {
    term: "Lithium disilicate",
    definition:
      "A glass-ceramic material — most commonly sold as e.max — used for veneers, crowns, and inlays. Strong enough for most anterior and many posterior restorations; more forgiving than feldspathic porcelain in higher-load situations.",
  },
  {
    term: "Margin",
    definition:
      "The edge of the preparation, where the veneer meets the tooth. A clean, well-finished margin protects the underlying tooth from decay and stain; a poor margin is visible as a dark line or a ledge, and is a common cause of early veneer failure.",
  },
  {
    term: "Minimal-prep veneer",
    definition:
      "A veneer that requires removal of less than roughly half a millimetre of enamel — often only enough to give the porcelain a key to bond to. Best suited to cases with agreeable starting shape, shade, and alignment.",
  },
  {
    term: "Mock-up",
    definition:
      "A temporary composite or resin preview of the proposed smile, placed directly on the unprepared teeth, that allows the patient to see and feel the planned result before any enamel is removed. The mock-up is the last clean stopping point before preparation begins.",
  },
  {
    term: "No-prep veneer",
    definition:
      "A veneer bonded to the tooth without cutting any enamel. Only appropriate in a narrow set of cases — usually where the teeth are already well-aligned and the target shape is additive rather than reductive.",
  },
  {
    term: "Occlusion",
    definition:
      "The way upper and lower teeth meet and move against each other during biting, chewing, and parafunction. A veneer case that ignores occlusion is at elevated risk of chipping, debonding, and shortened service life.",
  },
  {
    term: "Onlay",
    definition:
      "A restoration that covers one or more cusps of a tooth without covering the whole crown. Used when the damage is too extensive for an inlay but a full crown would remove more healthy tooth than necessary.",
  },
  {
    term: "Photographic documentation",
    definition:
      "Standardised clinical photographs — frontal, lateral, retracted, occlusal, full-face — taken at defined stages in the case. A clinic's photographic protocol is a reliable external signal of how seriously it plans and reviews cosmetic work.",
  },
  {
    term: "Refractory die",
    definition:
      "A heat-resistant model made from an impression of the prepared tooth, on which feldspathic porcelain can be built up and fired directly. A traditional technique for hand-layered veneers; largely replaced in high-volume labs by pressed and milled methods, but still used for premium aesthetic cases.",
  },
  {
    term: "Shade tab",
    definition:
      "A physical, calibrated ceramic sample used to match the target tooth colour. Shade-taking is usually done at the start of a case, under controlled lighting, before the tooth dehydrates from being isolated.",
  },
  {
    term: "Temporaries",
    definition:
      "Interim veneers or crowns placed at the end of the preparation appointment to protect the prepared teeth and let the patient live with the planned shape and shade for a few weeks before the final case is cemented. A functional test-drive of the design.",
  },
  {
    term: "Translucency",
    definition:
      "The degree to which light passes through a tooth or a restoration. Natural enamel is translucent at the biting edge; opaque ceramics that ignore this property produce the painted-on look associated with low-craft veneer work.",
  },
  {
    term: "Try-in",
    definition:
      "A staged appointment at which the finished veneers are placed on the teeth with water or try-in paste — not bonded — so patient, dentist, and ceramist can approve the fit, shade, and shape before permanent cementation. The last clean stopping point for adjustment.",
  },
  {
    term: "Value (colour)",
    definition:
      "In dental optics, the brightness of a tooth on a black-to-white axis, independent of hue or chroma. Value, not hue, is usually what makes a case read as convincingly light or unnaturally bleached.",
  },
  {
    term: "Veneer",
    definition:
      "A thin shell of porcelain or composite bonded to the front surface of a tooth to change its shape, shade, alignment, or surface texture. The umbrella term covers a range of preparation philosophies from no-prep to substantially reductive.",
  },
  {
    term: "Wax-up",
    definition:
      "A physical model of the proposed smile sculpted in wax on a cast of the patient's teeth. The wax-up is the source of the mock-up and, in pressed ceramic workflows, of the final restoration itself.",
  },
  {
    term: "Zirconia",
    definition:
      "A very strong ceramic material used for crowns, bridges, and implant restorations. Traditional zirconia is opaque and poorly suited to anterior veneers; newer layered and translucent zirconias have narrowed that gap, though feldspathic and lithium-disilicate ceramics remain preferred for premium anterior aesthetics.",
  },
];

export default function Glossary() {
  const definedTermSet = {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    name: "Spain Smile Guide glossary",
    url: `${SITE_URL}${PATH}`,
    description:
      "Plain-English definitions of terms used in cosmetic dentistry, published for patients evaluating clinics in Spain.",
    hasDefinedTerm: TERMS.map((t) => ({
      "@type": "DefinedTerm",
      name: t.term,
      description: t.definition,
      inDefinedTermSet: `${SITE_URL}${PATH}`,
    })),
  };

  return (
    <>
      <SEO
        title={TITLE}
        description={DESCRIPTION}
        path={PATH}
        type="website"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(definedTermSet)}
        </script>
      </Helmet>
      <BreadcrumbSchema
        items={[
          { name: "Home", path: "/" },
          { name: "Glossary", path: PATH },
        ]}
      />

      <ArticleHero
        kicker="Reference"
        title="Glossary"
        standfirst="The terms a patient will meet in a serious consultation about porcelain veneers, crowns, and smile design — defined in plain English, without marketing."
        datePublished="2026-04-18"
        dateModified="2026-04-18"
      />

      <div className="container-prose prose-editorial mt-10">
        <p>
          The vocabulary of cosmetic dentistry is half clinical, half
          commercial. A patient researching a case in Spain will meet
          "no-prep" and "feldspathic" and "DSD" in the same sentence, and
          will rarely be told which of those terms denote a material, a
          technique, a workflow, or a marketing category. The definitions
          below are the plain-English version. They are written for
          readers, not for practitioners, and they are deliberately short.
        </p>
        <p>
          Where a term has a narrow technical meaning and a looser
          commercial one, the technical meaning is given first.
        </p>

        <div className="mt-10 space-y-8">
          {TERMS.map((t) => (
            <section
              key={t.term}
              id={t.term.toLowerCase().replace(/[^a-z0-9]+/g, "-")}
              className="border-t border-rule pt-5"
            >
              <h3 className="font-serif text-h3 text-ink">{t.term}</h3>
              <p className="mt-2 text-ink">{t.definition}</p>
            </section>
          ))}
        </div>

        <hr className="rule" />
        <p className="text-small text-charcoal/70">
          Corrections and additions welcome — write to the editorial
          desk. Terms that become common in consultation rooms faster than
          this page can keep up are the ones most worth adding.
        </p>
      </div>
    </>
  );
}
